import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Sparkles, Coins, ArrowRight, X, Clock, Copy, Check, Tag } from 'lucide-react'
import { useOnboardingCelebration } from '../../store/onboardingCelebrationStore'
import { useCreditBalance } from '../../hooks/credits/useCreditBalance'
import { useEffect, useRef, useState } from 'react'
import { useAccountStore } from '../../store/accountStore'
import { useOnboardingAccountInsights } from '../../hooks/shared/onboarding/useOnboardingAccountInsights'
import { onboardingCompletionBonusDeadlineMs } from '../../lib/onboarding/onboardingRoiCardState'
import { trackOnboardingCompletionModalShown } from '../../lib/analytics/onboardingAnalytics'

/** Stripe promotion code (real). 48h window is UI urgency only. */
export const ONBOARDING_TUTORIAL_PROMO_CODE = 'Tutorial'
export const ONBOARDING_TUTORIAL_DISCOUNT_PERCENT = 5

function formatDeadline(deadlineMs: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(deadlineMs))
}

export function OnboardingCompletionModal() {
  const { t, i18n } = useTranslation('dashboard')
  const showModal = useOnboardingCelebration((s) => s.showModal)
  const pointsAdded = useOnboardingCelebration((s) => s.pointsAdded)
  const completionBonus = useOnboardingCelebration((s) => s.completionBonus)
  const dismissModal = useOnboardingCelebration((s) => s.dismissModal)
  const { data: balance } = useCreditBalance()
  const accountId = useAccountStore((s) => s.current?.id)
  const { data: insights } = useOnboardingAccountInsights(accountId, showModal)
  const trackedShown = useRef(false)
  const [copied, setCopied] = useState(false)

  const recommendedPlan = insights?.recommendedPlan ?? 'STANDARD'
  const totalOffers = insights?.totalOffers ?? 0
  const catalogHours = insights?.catalogHoursEstimate ?? 0
  const bonusDeadlineMs = onboardingCompletionBonusDeadlineMs()
  const higherPlanLabel = t('notifications.onboardingModal.plans.VIP')

  useEffect(() => {
    if (!showModal || trackedShown.current) return
    trackedShown.current = true
    trackOnboardingCompletionModalShown(recommendedPlan, totalOffers)
  }, [showModal, recommendedPlan, totalOffers])

  useEffect(() => {
    if (!showModal) setCopied(false)
  }, [showModal])

  if (!showModal) return null

  const total = balance?.total ?? pointsAdded
  const planLabel = t(`notifications.onboardingModal.plans.${recommendedPlan}`)

  const copyPromoCode = async () => {
    try {
      await navigator.clipboard.writeText(ONBOARDING_TUTORIAL_PROMO_CODE)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={dismissModal} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-orange-50 via-amber-50 to-white px-6 pt-8 pb-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FF5A00]/10">
            <Sparkles className="h-8 w-8 text-[#FF5A00]" />
          </div>
          <h2 className="mt-4 text-xl font-bold text-gray-900">
            {t('notifications.onboardingModal.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            {totalOffers > 0
              ? t('notifications.onboardingModal.subtitlePersonalized', {
                  count: totalOffers,
                  hours: catalogHours,
                })
              : t('notifications.onboardingModal.subtitle')}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-green-50 px-4 py-2 text-sm font-semibold text-green-700">
            <Coins className="h-4 w-4" />
            {t('notifications.onboardingModal.pointsEarned', {
              points: pointsAdded,
              bonus: completionBonus,
            })}
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50/80 px-4 py-3 text-left">
            <p className="text-sm font-semibold text-gray-900">
              {t('notifications.onboardingModal.recommendedPlanTitle', { plan: planLabel })}
            </p>
            <p className="mt-1 text-xs text-gray-600 leading-relaxed">
              {t(`notifications.onboardingModal.recommendedPlanBody.${recommendedPlan}`, {
                count: totalOffers,
                balance: total,
              })}
            </p>
            {recommendedPlan === 'STANDARD' && (
              <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                {t('notifications.onboardingModal.higherPlanHint', {
                  higherPlan: higherPlanLabel,
                })}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 text-left">
            <div className="flex items-start gap-2">
              <Tag className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {t('notifications.onboardingModal.promoTitle', {
                    percent: ONBOARDING_TUTORIAL_DISCOUNT_PERCENT,
                  })}
                </p>
                <p className="mt-1 text-xs text-gray-600 leading-relaxed">
                  {t('notifications.onboardingModal.promoBody')}
                </p>
                <div className="mt-2.5 flex items-center gap-2">
                  <code className="rounded-lg bg-white border border-emerald-200 px-3 py-1.5 text-sm font-bold tracking-wide text-emerald-900">
                    {ONBOARDING_TUTORIAL_PROMO_CODE}
                  </code>
                  <button
                    type="button"
                    onClick={() => void copyPromoCode()}
                    className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-50"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5" />
                        {t('notifications.onboardingModal.promoCopied')}
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" />
                        {t('notifications.onboardingModal.promoCopy')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {bonusDeadlineMs != null && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-left">
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900 leading-relaxed">
                  {t('notifications.onboardingModal.bonus48h', {
                    code: ONBOARDING_TUTORIAL_PROMO_CODE,
                    percent: ONBOARDING_TUTORIAL_DISCOUNT_PERCENT,
                    deadline: formatDeadline(bonusDeadlineMs, i18n.language),
                  })}
                </p>
              </div>
            </div>
          )}

          <Link
            to="/credits#plans"
            onClick={dismissModal}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#212121] px-4 py-3 text-sm font-semibold text-white hover:bg-[#333333] transition-colors"
          >
            {t('notifications.onboardingModal.ctaPrimaryPlan', {
              plan: planLabel,
              percent: ONBOARDING_TUTORIAL_DISCOUNT_PERCENT,
            })}
            <ArrowRight className="h-4 w-4" />
          </Link>

          {recommendedPlan === 'STANDARD' && (
            <Link
              to="/credits#plans"
              onClick={dismissModal}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors"
            >
              {t('notifications.onboardingModal.ctaHigherPlan', {
                plan: higherPlanLabel,
              })}
            </Link>
          )}

          <button
            type="button"
            onClick={dismissModal}
            className="w-full text-center text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            {t('notifications.onboardingModal.ctaSecondary')}
          </button>
        </div>

        <button
          type="button"
          onClick={dismissModal}
          className="absolute top-3 right-3 rounded-lg p-1.5 text-gray-400 hover:bg-white/80 hover:text-gray-600"
          aria-label={t('common:actions.close', { defaultValue: 'Zamknij' })}
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}
